import React, {useCallback, useEffect, useRef, useState} from 'react';

import {prepareNumericString, toCurrencyAmount} from '@ELib/utilities/string';

import {ETextInput, ETextInputProps} from './ETextInput';

export const EAmountInput = ({
  onBlur,
  onChange,
  onFocus,
  value,
  name,
  autoFocus,
  ...props
}: ETextInputProps): React.ReactElement => {
  const [validNumber, setValidNumber] = useState('');
  const respectedAutoFocus = useRef(false);

  useEffect(() => {
    if (value === validNumber) {
      return;
    }
    if (respectedAutoFocus.current) {
      setValidNumber(toCurrencyAmount(value));
    } else {
      setValidNumber(autoFocus ? value || '0.00' : toCurrencyAmount(value));
      respectedAutoFocus.current = true;
    }
    // should only trigger on `value` change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = e.target.value.trim();
      if (isValidNumber(e.target.value) || e.target.value === '-') {
        setValidNumber(e.target.value);
        onChange?.(e);
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      const numberToUse = isValidNumber(validNumber) ? validNumber : '';

      if (onChange) {
        const clonedEvent = new Event('input');
        Object.defineProperty(clonedEvent, 'target', {
          writable: true,
          value: {value: numberToUse, name},
        });
        onChange(clonedEvent as unknown as React.ChangeEvent<HTMLInputElement>);
      }
      onBlur?.(e);

      const amount = numberToUse ? toCurrencyAmount(numberToUse) : '';
      setValidNumber(amount);
    },
    [name, onBlur, onChange, validNumber]
  );

  return (
    <ETextInput
      {...props}
      autoFocus={autoFocus}
      name={name}
      selectTextOnFocus={true}
      value={validNumber}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={(e) => {
        // remove currency format to avoid handling of apostrophes during editing
        setValidNumber(prepareNumericString(validNumber));
        onFocus?.(e);
      }}
    />
  );
};

const isValidNumber = (number: string) => !Number.isNaN(Number(number));
